import { useTranslation } from "react-i18next"
import Skeleton from "react-loading-skeleton"

import { UnauthenticatedPageLayout } from "@/components/layouts/UnauthenticatedPageLayout"
import { useAppContext } from "@/contexts/app-context"
import { useCollection } from "@/hooks/resources"
import ProductPrice from "@/types/product-price"
import { SelectMembershipForm } from "@/components/forms/select-membership-form"
import UserType from "@/types/user-type"

const SelectMembershipPage = () => {
  const { t } = useTranslation("select-membership-page")
  const { state: { currentUser, isLoadingCurrentUser } } = useAppContext()

  const [availableProductPrices, { isLoading: isLoadingAvailableProductPrices }] =
    useCollection<ProductPrice>("/products/prices")

  // Sort plans: basic plans first (monthly, then quarterly), then premium plans (monthly, then quarterly)
  const sortedProductPrices = availableProductPrices ? [...availableProductPrices].sort((a, b) => {
    const order: Record<string, number> = {
      "basic_monthly": 1,
      "basic_quarterly": 2,
      "premium_monthly": 3,
      "premium_quarterly": 4,
    };
    return (order[a.lookupKey] || 999) - (order[b.lookupKey] || 999);
  }) : availableProductPrices

  return (
    <UnauthenticatedPageLayout
      title={t("pageTitle")}
      className="flex flex-col gap-4 items-center py-8 w-full"
    >
      {
        isLoadingAvailableProductPrices || isLoadingCurrentUser ? [...Array(2)].map((_, index) => (
          <div
            key={index}
            className={`bg-white rounded-2xl border-4 w-[320px] pb-4 flex flex-col gap-4 items-center overflow-clip`}
          >
            <Skeleton
              borderRadius={0}
              containerClassName="w-full leading-none"
              className="h-12 w-full"
            />

            <div className="flex flex-col gap-4 px-4 items-center w-full">
              <Skeleton
                containerClassName="w-3/4 leading-none"
                className="h-8"
              />

              <Skeleton
                containerClassName="w-5/6"
                className="h-4"
                count={5}
              />

              <Skeleton
                containerClassName="w-full leading-none"
                className="h-11"
              />

              <Skeleton
                containerClassName="w-1/2 leading-none"
                className="h-6"
              />
            </div>
          </div>
        )) : (
          <div className="flex flex-col gap-4 w-full max-w-4xl">
            {/* Basic plans row */}
            <div className="flex flex-wrap gap-4 justify-center">
              {(sortedProductPrices || [])
                .filter(price => price.lookupKey.startsWith("basic_"))
                .map(productPrice => (
                  <SelectMembershipForm 
                    key={productPrice.lookupKey} 
                    productPrice={productPrice} 
                    currentUser={currentUser as UserType} 
                  />
                ))}
            </div>
            {/* Premium plans row */}
            <div className="flex flex-wrap gap-4 justify-center">
              {(sortedProductPrices || [])
                .filter(price => price.lookupKey.startsWith("premium_"))
                .map(productPrice => (
                  <SelectMembershipForm 
                    key={productPrice.lookupKey} 
                    productPrice={productPrice} 
                    currentUser={currentUser as UserType} 
                  />
                ))}
            </div>
          </div>
        )
      }
    </UnauthenticatedPageLayout>
  )
}

export default SelectMembershipPage
